import { motion } from 'framer-motion';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/feature/SEOHead';
import { Link } from 'react-router-dom';
import HeroBackground from '../../components/feature/HeroBackground';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const servicesSchema = [
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${SITE_URL}/services`,
    url: `${SITE_URL}/services`,
    name: 'Supply Chain Services – Sourcing, Logistics & AI | True Passage Ventures',
    description: 'End-to-end supply chain services including product sourcing, design & development, quality control, export logistics, AI solutions, and dedicated support.',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Services', item: `${SITE_URL}/services` },
      ],
    },
    publisher: {
      '@type': 'Organization',
      name: 'True Passage Ventures',
      url: SITE_URL,
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Global Supply Chain & Sourcing Services',
    url: `${SITE_URL}/services`,
    provider: {
      '@type': 'Organization',
      name: 'True Passage Ventures',
      url: SITE_URL,
      logo: {
        '@type': 'ImageObject',
        url: `${SITE_URL}/vite.svg`,
      },
    },
    description: 'End-to-end supply chain services including product sourcing, design & development, quality control, export logistics, AI solutions, and dedicated support.',
    serviceType: [
      'Product Sourcing',
      'Design & Development',
      'Quality Control',
      'Export Logistics',
      'AI Solutions',
      'Dedicated Support',
    ],
    areaServed: {
      '@type': 'Place',
      name: 'Worldwide',
    },
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Supply Chain Services',
      itemListElement: [
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Product Sourcing',
            description: 'Connect with verified Indian manufacturers across packaging, industrial components, plastics, OEM engineering, and home products.',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Design & Development',
            description: 'Transform your product ideas into reality with end-to-end design and development services.',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Quality Control',
            description: 'Comprehensive quality inspection services ensuring products meet international standards.',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Export Logistics',
            description: 'Expert logistics management including documentation, customs, and delivery coordination.',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'AI Solutions',
            description: 'AI-powered tools providing insights and automation for smarter supply chain decision-making.',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Dedicated Support',
            description: 'Personalized assistance from our Canada-based team throughout your sourcing journey.',
          },
        },
      ],
    },
  },
];

const ServicesPage = () => {
  const services = [
    {
      icon: 'ri-search-line',
      title: 'Product Sourcing',
      description: 'Connect with verified Indian manufacturers across packaging, industrial components, plastics, OEM engineering, and home products. We identify the right suppliers for your specific requirements.',
      features: [
        'Supplier verification and vetting',
        'Price negotiation and comparison',
        'Sample procurement and testing',
        'Custom product specifications',
      ],
    },
    {
      icon: 'ri-pencil-ruler-2-line',
      title: 'Design & Development',
      description: 'Transform your product ideas into reality with our end-to-end design and development services. From concept to prototype, we work with experienced engineers and designers.',
      features: [
        'Product design and engineering',
        'Prototype development',
        'Material selection guidance',
        'Manufacturing feasibility analysis',
      ],
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Quality Control',
      description: 'Ensure your products meet international standards with comprehensive quality inspection services. We conduct thorough checks at every stage of production.',
      features: [
        'Pre-production inspections',
        'In-process quality monitoring',
        'Final product verification',
        'Compliance and certification support',
      ],
    },
    {
      icon: 'ri-ship-line',
      title: 'Export Logistics',
      description: 'Navigate the complexities of international shipping with our expert logistics management. We handle documentation, customs, and delivery coordination.',
      features: [
        'Freight forwarding and shipping',
        'Customs clearance assistance',
        'Documentation and compliance',
        'Delivery tracking and coordination',
      ],
    },
    {
      icon: 'ri-robot-line',
      title: 'AI Solutions',
      description: 'Leverage cutting-edge artificial intelligence to optimize your supply chain operations. Our AI-powered tools provide insights and automation for smarter decision-making.',
      features: [
        'Demand forecasting and planning',
        'Supplier performance analytics',
        'Automated quality detection',
        'Supply chain optimization',
      ],
    },
    {
      icon: 'ri-customer-service-2-line',
      title: 'Dedicated Support',
      description: 'Receive personalized assistance from our Canada-based team throughout your sourcing journey. We provide ongoing support to ensure smooth operations.',
      features: [
        '24-hour response time guarantee',
        'Multilingual communication support',
        'Project management and coordination',
        'Long-term partnership development',
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Supply Chain Services – Sourcing, Logistics & AI | True Passage Ventures"
        description="Comprehensive supply chain services from True Passage Ventures: product sourcing, quality control, export logistics, OEM manufacturing, and custom AI tools. End-to-end solutions from India."
        keywords="supply chain services, product sourcing India, export logistics, quality control, OEM manufacturing, AI supply chain"
        canonical="/services"
        schema={servicesSchema}
      />
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-navy-primary via-navy-secondary to-navy-primary overflow-hidden">
        <HeroBackground />
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12">
          <motion.div
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Comprehensive Supply Chain
              <br />
              <span className="text-gold-accent">Services</span>
            </h1>
            <p className="text-xl text-white/70 leading-relaxed">
              From sourcing to delivery, we provide end-to-end solutions for your global trade needs
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                className="bg-white border-2 border-navy-primary/10 rounded-2xl p-8 hover:border-gold-accent hover:shadow-xl transition-all"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <div className="w-16 h-16 bg-gold-accent/10 rounded-xl flex items-center justify-center mb-6">
                  <i className={`${service.icon} text-gold-accent text-3xl`}></i>
                </div>

                <h3 className="text-2xl font-bold text-navy-primary mb-4">
                  {service.title}
                </h3>

                <p className="text-base text-navy-primary/70 leading-relaxed mb-6">
                  {service.description}
                </p>

                <ul className="space-y-3">
                  {service.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-gold-accent text-lg mt-0.5 flex-shrink-0"></i>
                      <span className="text-sm text-navy-primary/80">{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#0B1F33] text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Let's discuss how we can support your global sourcing needs
          </p>
          <Link
            to="/request-quote"
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#D4AF37] text-[#0B1F33] rounded-lg font-semibold text-lg hover:bg-[#D4AF37]/90 transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-file-list-3-line"></i>
            Request Quote
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesPage;
